<template>
  <b-col>
    <b-row>
      <b-col cols="12">
        <div class="d-flex justify-content-between align-items-center pt-50 mb-2">
          <div>
            <h2 class="font-weight-bolder m-0">
              Trazabilidad de jornadas de usuarios
            </h2>
          </div>
          <div class="d-flex align-items-center ml-2">
            <span
              class="cursor-pointer"
              @click="toggleFilterSidebar"
            >
              <feather-icon
                icon="FilterIcon"
                size="20"
              />
            </span>
          </div>
        </div>
      </b-col>
      <b-col cols="12">
        <!-- Sidebar de filtros -->
        <filter-sidebar
          v-model="showFilterSidebar"
          :has-active-filters="hasActiveFilters"
          :active-filters="computedActiveFilters"
          @apply-filters="handleSearch"
          @clear-filters="clearFilters"
          @remove-filter="removeActiveFilter"
        >
          <!-- Slot de filtros -->
          <template #filters>
            <b-form-group
              label="Búsqueda"
              label-for="search-input"
            >
              <b-input-group class="input-group">
                <b-input-group-prepend is-text>
                  <feather-icon icon="SearchIcon" />
                </b-input-group-prepend>
                <b-form-input
                  id="search-input"
                  v-model="searchTerm"
                  type="text"
                  :placeholder="'Buscar'"
                  @keydown.enter.native="handleSearch"
                />
              </b-input-group>
            </b-form-group>
            <b-form-group
              label="Fecha inicio"
              label-for="filter-fechaini"
            >
              <flat-pickr
                v-model="date_ini"
                name="fecha_ini"
                class="form-control"
              />
            </b-form-group>
            <b-form-group
              label="Fecha fin"
              label-for="filter-fechafin"
            >
              <flat-pickr
                v-model="date_fin"
                name="fecha_fin"
                class="form-control"
              />
            </b-form-group>
          </template>
        </filter-sidebar>
      </b-col>
    </b-row>
    <b-card no-body>
      <b-table
        hover
        responsive
        :items="items"
        :fields="columns"
      >
        <template #cell(user)="data">
          <div class="d-flex align-items-center">
            <span>
              <b-link
                v-if="data.item.user"
                :to="{ name: 'viewUser', params: { id: data.item.user_id } }"
                class="link"
              >
                {{ data.item.user.name }} {{ data.item.user.surname }}
              </b-link>
            </span>
          </div>
        </template>
        <template #cell(journey_user)="data">
          <div class="d-flex align-items-center">
            <span>
              <b-link
                v-if="data.item.journey_user"
                :to="{ name: 'viewUser', params: { id: data.item.journey_user_id } }"
                class="link"
              >
                {{ data.item.journey_user.name }} {{ data.item.journey_user.surname }}
              </b-link>
            </span>
          </div>
        </template>
        <template #cell(day)="data">
          {{ TimestampToFlatPickrWithTime(data.item.created_at) }}
        </template>
        <template #cell(action)="data">
          {{ translateAction(data.item.action) }}
        </template>
      </b-table>
      <div
        class="d-flex align-items-center justify-content-between mb-1 px-2"
      >
        <div class="d-flex align-items-center mb-0">
          <span class="text-nowrap"> {{ "Por página" }}: </span>
          <b-form-select
            v-model="pageLength"
            :options="pages"
            class="ml-50 mr-1"
            @input="handlePageChange"
          />
          <span class="text-nowrap"> {{ "Total" }}: {{ totalItems }}</span>
        </div>
        <div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalItems"
            :per-page="pageLength"
            first-number
            last-number
            align="right"
            prev-class="prev-item"
            next-class="next-item"
            class="mt-1 mb-0"
            @change="handleChangePage"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>
        </div>
      </div>
    </b-card>
  </b-col>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  BPagination,
  BFormInput,
  BFormSelect,
  BCard,
  BTable,
  BCol,
  BRow,
  BLink,
  BInputGroup,
  BInputGroupPrepend,
  BFormGroup,
} from 'bootstrap-vue'
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import flatPickr from 'vue-flatpickr-component'
import { FlatPickrToTimestamp, TimestampToFlatPickrWithTime } from '@/libs/helpers'
import FilterSidebar from '@/components/ui/FilterSidebar.vue'

export default {
  components: {
    BCard,
    BPagination,
    BFormInput,
    BFormSelect,
    BTable,
    BCol,
    BRow,
    BLink,
    BInputGroup,
    BInputGroupPrepend,
    BFormGroup,
    flatPickr,
    FilterSidebar,
  },
  data() {
    return {
      currentPage: 1,
      pageLength: 25,
      pages: ['15', '20', '25', '50', '100'],
      columns: [
        {
          label: 'Usuario',
          key: 'user',
        },
        {
          label: 'Editor',
          key: 'journey_user',
        },
        {
          label: 'Fecha',
          key: 'day',
        },
        {
          label: 'Acción',
          key: 'action',
        },
        {
          label: 'Motivo',
          key: 'reason',
        },
      ],
      searchTerm: '',
      date_ini: '',
      date_fin: '',
      appliedSearchTerm: '',
      appliedDateIni: '',
      appliedDateFin: '',
      showFilterSidebar: false,
    }
  },
  computed: {
    ...mapGetters({
      items: 'users/getJourneyLogs',
      totalItems: 'users/getTotalJourneyLogs',
      currentClient: 'clients/getCurrentClient',
    }),
    hasActiveFilters() {
      return this.appliedSearchTerm !== '' || this.appliedDateIni !== '' || this.appliedDateFin !== ''
    },
    computedActiveFilters() {
      const filters = []
      if (this.appliedSearchTerm) {
        filters.push({
          label: 'Búsqueda',
          value: this.appliedSearchTerm,
          icon: 'SearchIcon',
          filterType: 'search',
        })
      }
      if (this.appliedDateIni) {
        filters.push({
          label: 'Desde',
          value: this.appliedDateIni,
          icon: 'CalendarIcon',
          filterType: 'dateIni',
        })
      }
      if (this.appliedDateFin) {
        filters.push({
          label: 'Hasta',
          value: this.appliedDateFin,
          icon: 'CalendarIcon',
          filterType: 'dateFin',
        })
      }
      return filters
    },
  },
  created() {
    this.chargeData()
  },
  methods: {
    TimestampToFlatPickrWithTime,
    ...mapActions({
      list: 'users/getJourneyLogs',
    }),
    translateAction(action) {
      const translations = {
        update: 'Actualización',
        send_notification: 'Notificación enviada',
        acept_notification: 'Notificación aceptada',
        out_of_schedule_check_in: 'Fichaje fuera de horario - entrada',
        out_of_schedule_check_out: 'Fichaje fuera de horario - salida',
      }
      return translations[action] || action
    },
    chargeData() {
      let filterDateIni = ''
      let filterDateFin = ''
      if (this.date_ini) {
        filterDateIni = FlatPickrToTimestamp(this.date_ini)
      }
      if (this.date_fin) {
        filterDateFin = FlatPickrToTimestamp(this.date_fin)
      }
      this.list({
        page: this.currentPage,
        per_page: this.pageLength,
        search: this.searchTerm,
        date_ini: filterDateIni,
        date_fin: filterDateFin,
        client_id: this.currentClient.id,
      })
    },
    handleSearch() {
      this.currentPage = 1
      this.appliedSearchTerm = this.searchTerm
      this.appliedDateIni = this.date_ini
      this.appliedDateFin = this.date_fin
      this.chargeData()
    },
    clearFilters() {
      this.searchTerm = ''
      this.date_ini = ''
      this.date_fin = ''
      this.appliedSearchTerm = ''
      this.appliedDateIni = ''
      this.appliedDateFin = ''
      this.currentPage = 1
      this.chargeData()
    },
    removeActiveFilter(idx) {
      const f = this.computedActiveFilters[idx]
      if (f && f.filterType === 'search') {
        this.appliedSearchTerm = ''
        this.searchTerm = ''
      } else if (f && f.filterType === 'dateIni') {
        this.appliedDateIni = ''
        this.date_ini = ''
      } else if (f && f.filterType === 'dateFin') {
        this.appliedDateFin = ''
        this.date_fin = ''
      }
      this.currentPage = 1
      this.chargeData()
    },
    toggleFilterSidebar() {
      this.showFilterSidebar = !this.showFilterSidebar
    },
    handleChangePage(page) {
      this.currentPage = page
      this.chargeData()
    },
    handlePageChange() {
      this.currentPage = 1
      this.chargeData()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.dropdown-user {
  list-style-type: none
}

[dir] .b-nav-dropdown .dropdown-toggle::after {
  display: none
}
</style>
